/**
 * "Meeting series" section on the meeting record: lists the other occurrences
 * of the meeting's recurring series (most-recent-first). Each occurrence renders
 * as the shared `MeetingCard` (matching "Upcoming meetings", but with the
 * company badge suppressed so only the Summary/Prep tag shows) and opens that
 * meeting in a slideout on click, tagged "Summary" when it has a summary
 * (prioritised) or "Meeting prep" when it has prep. Rows are paginated with a
 * page picker. A "Filter" button in the section header opens a popover
 * (mirroring the timeline filter) with a start/end date range and a
 * "With summaries only" toggle. Renders nothing when the meeting is not part of
 * a series.
 */

import { getLocalTimeZone } from '@internationalized/date';
import { ArrowLeft, ArrowRight, FilterLines } from '@untitledui/icons';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { DateValue, RangeValue } from 'react-aria-components';
import { Dialog as AriaDialog } from 'react-aria-components';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { DialogTrigger } from '@/lib/base/overlays/modal';
import { Popover } from '@/lib/base/overlays/popover';
import { Pagination } from '@/lib/base/pagination';
import { DateRangePicker } from '@/lib/base/pickers';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import { useOpenSlideoutOptional } from '../../hooks/useOpenSlideout';
import {
  filterSeriesOccurrences,
  type MeetingSeriesOccurrence,
  occurrenceHasMeetingPrep,
  occurrenceHasSummary,
  useMeetingSeries,
} from '../hooks/useMeetingSeries';
import { MeetingCard } from '../registry/entityCards';
import { CollapsibleSection } from './collapsible-section';

/** Occurrences shown per page. */
const SERIES_PAGE_SIZE = 3;

/**
 * The status pill shown on an occurrence row: a "Summary" tag when the meeting
 * has a summary (prioritised over prep), otherwise a "Meeting prep" tag when it
 * has prep, otherwise nothing.
 */
function occurrenceTag(occurrence: MeetingSeriesOccurrence): ReactNode {
  if (occurrenceHasSummary(occurrence)) {
    return (
      <Badge type='pill-color' size='sm' color='success'>
        Summary
      </Badge>
    );
  }
  if (occurrenceHasMeetingPrep(occurrence)) {
    return (
      <Badge type='pill-color' size='sm' color='gray'>
        Meeting prep
      </Badge>
    );
  }
  return null;
}

interface MeetingSeriesFiltersProps {
  dateRange: RangeValue<DateValue> | null;
  onDateRangeChange: (value: RangeValue<DateValue> | null) => void;
  onlyWithSummaries: boolean;
  onOnlyWithSummariesChange: (isSelected: boolean) => void;
  onClear: () => void;
}

/**
 * The "Filter" button + popover for the meeting-series section. Mirrors the
 * timeline filter (`TimelineFilters`): a link-style button with an
 * active-filter-count badge that opens a `Popover` holding the date range and
 * "With summaries only" controls.
 */
function MeetingSeriesFilters({
  dateRange,
  onDateRangeChange,
  onlyWithSummaries,
  onOnlyWithSummariesChange,
  onClear,
}: MeetingSeriesFiltersProps) {
  const activeFilterCount = (dateRange ? 1 : 0) + (onlyWithSummaries ? 1 : 0);

  return (
    <DialogTrigger>
      <Button
        color='link-gray'
        size='sm'
        iconLeading={FilterLines}
        className='!text-fg-brand-primary hover:!text-utility-brand-700 !py-1 !px-2 !-my-1 !font-medium [&_svg]:!size-3.5'
      >
        <span className='inline-flex items-center gap-1.5'>
          Filter
          {activeFilterCount > 0 && (
            <Badge size='sm' color='brand'>
              {activeFilterCount}
            </Badge>
          )}
        </span>
      </Button>

      <Popover placement='bottom end' size='md' className='w-72'>
        {/* The Dialog takes DialogTrigger's aria-controls id and focus, so the
            trigger toggles the popover and Escape closes it (see popover.tsx). */}
        <AriaDialog
          aria-label='Meeting series filters'
          className='outline-hidden'
        >
          <div className='flex items-center justify-between border-b border-secondary_alt px-3 py-2.5'>
            <span className='text-sm font-semibold text-secondary'>
              Filters
            </span>
            {activeFilterCount > 0 && (
              <Button
                color='link-gray'
                size='sm'
                onClick={onClear}
                className='!-mr-1.5 !px-1.5 !text-xs !font-medium'
              >
                Clear all
              </Button>
            )}
          </div>

          <div className='flex flex-col gap-3 px-3 py-3'>
            <div className='flex flex-col gap-1.5 [&_button]:w-full [&_button]:justify-start'>
              <span className='text-xs font-medium uppercase tracking-wide text-quaternary'>
                Date range
              </span>
              <DateRangePicker
                aria-label='Filter series by date'
                value={dateRange}
                onChange={onDateRangeChange}
              />
            </div>

            <Checkbox
              isSelected={onlyWithSummaries}
              onChange={onOnlyWithSummariesChange}
              label='With summaries only'
              size='sm'
            />
          </div>
        </AriaDialog>
      </Popover>
    </DialogTrigger>
  );
}

/**
 * Compact page picker for the series list. Composes the base `Pagination`
 * primitive (rather than a prebuilt variant) so prev/next are xs icon-only
 * buttons and the page numbers are small — sized for a slideout section.
 */
function SeriesPagination({
  page,
  total,
  onPageChange,
}: {
  page: number;
  total: number;
  onPageChange: (nextPage: number) => void;
}) {
  return (
    <Pagination.Root
      page={page}
      total={total}
      onPageChange={onPageChange}
      className='flex items-center justify-center gap-1 pt-1'
    >
      <Pagination.PrevTrigger asChild>
        <Button color='tertiary' size='xs' iconLeading={ArrowLeft} />
      </Pagination.PrevTrigger>

      <Pagination.Context>
        {({ pages }) => (
          <div className='flex items-center gap-0.5'>
            {pages.map(item =>
              item.type === 'page' ? (
                <Pagination.Item
                  key={item.value}
                  value={item.value}
                  isCurrent={item.isCurrent}
                  className={({ isSelected }) =>
                    cx(
                      'flex size-7 items-center justify-center rounded-md text-xs font-medium text-quaternary transition hover:bg-primary_hover hover:text-secondary',
                      isSelected && 'bg-primary_hover text-secondary'
                    )
                  }
                >
                  {item.value}
                </Pagination.Item>
              ) : (
                <Pagination.Ellipsis
                  key={item.key}
                  className='flex size-7 items-center justify-center text-xs text-tertiary'
                >
                  &#8230;
                </Pagination.Ellipsis>
              )
            )}
          </div>
        )}
      </Pagination.Context>

      <Pagination.NextTrigger asChild>
        <Button color='tertiary' size='xs' iconLeading={ArrowRight} />
      </Pagination.NextTrigger>
    </Pagination.Root>
  );
}

export function MeetingSeriesSection({ object }: { object: CrmObject }) {
  const { seriesId, occurrences, isLoading, error, refetch } = useMeetingSeries(
    object.org_id,
    object
  );
  const openSlideout = useOpenSlideoutOptional();

  const handleOpenMeeting = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(
    null
  );
  const [onlyWithSummaries, setOnlyWithSummaries] = useState(false);
  const [page, setPage] = useState(1);

  const filteredOccurrences = useMemo(() => {
    // The date range is inclusive of the whole end day: convert the end date to
    // the start of the following day and treat the upper bound as exclusive.
    const startMs = dateRange?.start
      ? dateRange.start.toDate(getLocalTimeZone()).getTime()
      : undefined;
    const endMs = dateRange?.end
      ? dateRange.end.add({ days: 1 }).toDate(getLocalTimeZone()).getTime()
      : undefined;
    return filterSeriesOccurrences(occurrences, {
      startMs,
      endMs,
      onlyWithSummaries,
    });
  }, [occurrences, dateRange, onlyWithSummaries]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOccurrences.length / SERIES_PAGE_SIZE)
  );
  // Clamp in case a filter shrank the list below the previously-selected page.
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * SERIES_PAGE_SIZE;
  const visibleOccurrences = filteredOccurrences.slice(
    pageStart,
    pageStart + SERIES_PAGE_SIZE
  );

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  // Reset to the first page whenever a filter changes so the newly-filtered
  // list starts from page 1 rather than a stale deep page.
  const handleDateRangeChange = useCallback(
    (value: RangeValue<DateValue> | null) => {
      setDateRange(value);
      setPage(1);
    },
    []
  );

  const handleOnlyWithSummariesChange = useCallback((isSelected: boolean) => {
    setOnlyWithSummaries(isSelected);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setDateRange(null);
    setOnlyWithSummaries(false);
    setPage(1);
  }, []);

  if (!seriesId) {
    return null;
  }

  if (isLoading) {
    return (
      <CollapsibleSection title='Meeting series' defaultExpanded>
        <div className='py-2' aria-busy='true'>
          <div className='h-4 md:h-5 w-32 bg-[var(--color-bg-tertiary)] rounded animate-pulse' />
        </div>
      </CollapsibleSection>
    );
  }

  if (error && occurrences.length === 0) {
    return (
      <CollapsibleSection title='Meeting series' defaultExpanded>
        <p className='text-xs md:text-sm text-fg-tertiary py-2'>
          We couldn't load the rest of this series.
        </p>
        <Button color='link-gray' size='sm' onClick={handleRetry}>
          Retry
        </Button>
      </CollapsibleSection>
    );
  }

  if (occurrences.length === 0) {
    return (
      <CollapsibleSection title='Meeting series' defaultExpanded>
        <p className='text-xs md:text-sm text-fg-tertiary py-2'>
          No other meetings in this series yet.
        </p>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      title='Meeting series'
      defaultExpanded
      actions={
        <MeetingSeriesFilters
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onlyWithSummaries={onlyWithSummaries}
          onOnlyWithSummariesChange={handleOnlyWithSummariesChange}
          onClear={handleClearFilters}
        />
      }
    >
      <div className='space-y-2'>
        {filteredOccurrences.length === 0 ? (
          <p className='text-xs md:text-sm text-fg-tertiary py-2'>
            No meetings match these filters.
          </p>
        ) : (
          <div className='space-y-2'>
            {visibleOccurrences.map(o => (
              <MeetingCard
                key={o.meeting.entity_id}
                object={o.meeting}
                onOpen={handleOpenMeeting}
                trailing={occurrenceTag(o)}
                showCompany={false}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <SeriesPagination
            page={currentPage}
            total={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </CollapsibleSection>
  );
}
